import React, { useContext, useEffect, useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, Button, Callout, DataListItem, Input, Input2FA } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import RequiredAppCallout from 'components/RequiredAppCallout'
import { TWO_FA_REQUIRED_LENGTH } from 'components/TwoFA/TwoFAField'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BeneficiaryDetailsCurrency, BeneficiaryType } from 'types/beneficiaries'
import { FiatSendStep } from 'types/walletActions'
import { getBeneficiaryName } from 'utils/beneficiaries'
import { roundNumberWithCommas } from 'utils/utils'
import {
  fiatSendFormAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from '../../Pay/atoms/atoms'
import { usePayoutSubmit } from '../hooks/useSubmitPayout'

interface ConfirmationProps {
  setStep: React.Dispatch<React.SetStateAction<FiatSendStep>>
}

export const Confirmation = ({ setStep }: ConfirmationProps) => {
  const { t } = useTranslation()
  const { twoFactorEnabled } = useTwoFAContext()

  const [walletAction] = useContext(WalletActionContext)
  const selectedWallet = useAtomValue(selectedWalletAtom)
  const fiatBeneficiary = useAtomValue(selectedFiatBeneficiaryAtom)
  const form = useAtomValue(fiatSendFormAtom)

  const { formValues, setValue, register } = form || {}

  const isFirstPartyPayout = fiatBeneficiary?.beneficiaryType === BeneficiaryType.FIRST_PARTY
  const isGBPBeneficiary = fiatBeneficiary?.currency === BeneficiaryDetailsCurrency.GBP
  const isCustomerWallet = !!walletAction?.meta?.customerWallet

  const {
    onSubmitPayoutHandler,
    isLegacyPayoutLoading,
    isPayoutRequestLoading,
    isPayoutAuthRequestLoading,
    calloutError,
  } = usePayoutSubmit({
    fiatBeneficiary,
    selectedWallet,
    formValues,
    form,
    setStep,
  })

  useEffect(() => {
    const password = formValues?.twoFactorCode
    if (password?.length === TWO_FA_REQUIRED_LENGTH && twoFactorEnabled) {
      onSubmitPayoutHandler()
    }
  }, [formValues?.twoFactorCode, twoFactorEnabled])

  const handleOn2FAChange = (value: string) => {
    setValue('twoFactorCode', value)
  }

  const handleClose = async () => {
    walletAction?.onClose()
  }

  const dataItems = useMemo(() => {
    const amount = Number(formValues?.amount ?? 0)
    const fee = Number(formValues?.fee ?? 0)
    const customerFee = Number(formValues?.customerFee ?? 0)
    const total = amount + fee + customerFee

    return [
      {
        id: 'paymentAmount',
        title: t(isFirstPartyPayout ? 'amountToWithdraw' : 'wallets.fiatSend.paymentAmount'),
        body: `${roundNumberWithCommas(total)} ${selectedWallet?.currency}`,
      },
      {
        id: 'fee',
        title: t('wallets.fiatSend.fee'),
        body: `${roundNumberWithCommas(fee)} ${selectedWallet?.currency}`,
      },
      ...(walletAction?.meta?.customerWallet
        ? [
            {
              id: 'customerFee',
              title: t('wallets.fiatSend.customerFee'),
              body: `${roundNumberWithCommas(customerFee)} ${selectedWallet?.currency}`,
            },
          ]
        : []),
      {
        id: 'amountToBeReceived',
        title: t('wallets.fiatSend.amountToBeReceived'),
        body: `${roundNumberWithCommas(amount)} ${selectedWallet?.currency}`,
      },
      {
        id: 'fromWallet',
        title: t('wallets.fiatSend.fromWallet'),
        body: selectedWallet?.walletName,
      },
      {
        id: 'toBeneficiary',
        title: t('wallets.fiatSend.toBeneficiary'),
        body: `${getBeneficiaryName(fiatBeneficiary)} | ${ellipseMiddleText(fiatBeneficiary?.fiat?.accountNumber, 6, 6)}`,
      },
      {
        id: 'paymentReference',
        title: t('wallets.fiatSend.paymentReference'),
        body: formValues?.paymentReference,
      },
      ...(formValues?.achSameDay
        ? [
            {
              id: 'achSameDay',
              title: t('wallets.fiatSend.sameDayAchPayment'),
              body: t('yes'),
            },
          ]
        : []),
    ]
  }, [formValues, selectedWallet, fiatBeneficiary, t, isFirstPartyPayout])

  return (
    <Box flexCol width="full" gapY={12}>
      {!!calloutError && (
        <Box>
          <Callout variant="error">{calloutError}</Callout>
        </Box>
      )}
      <Box>
        {dataItems?.map(item => (
          <DataListItem key={item?.id} id={item?.id} title={item?.title} body={item?.body} />
        ))}
      </Box>

      {isGBPBeneficiary && !isCustomerWallet && (
        <Box>
          <RequiredAppCallout />
        </Box>
      )}

      <Box width="full" paddingB={12}>
        {twoFactorEnabled ? (
          <Input2FA
            testId="add-fiat-instrument-2fa"
            onChange={handleOn2FAChange}
            value={formValues?.twoFactorCode}
            loading={isLegacyPayoutLoading || isPayoutRequestLoading || isPayoutAuthRequestLoading}
          />
        ) : (
          <>
            <Input
              placeholder={t('password')}
              type="password"
              testId="password"
              {...register('twoFactorCode')}
              onChange={e => {
                form.setValue('twoFactorCode', e.target.value)
                form.clearErrors('twoFactorCode')
              }}
              value={formValues?.twoFactorCode}
              error={!!form.formState?.errors?.twoFactorCode}
              errorText={form.formState?.errors?.twoFactorCode?.message}
              label=""
            />
            <Box flex justify="between" paddingT={20}>
              <Button variant="secondary" onClick={handleClose}>
                {t('wallets.fiatSend.cancel')}
              </Button>
              <Button variant="primary" onClick={onSubmitPayoutHandler}>
                {t('wallets.fiatSend.confirm')}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
